import * as dappService from '@/services/dapp-builder.service';
import type {
    DappBuilderStep,
    DappCategory,
    DappChain,
    DappComponentBlock,
    DappFramework,
    DappGeneratedFile,
    DappPage,
    DappProject,
    DappTemplate,
    DappTheme,
} from '@/types/dapp-builder';
import { create } from 'zustand';

/* ================================================================== */
/*  dApp Builder Store                                                */
/*                                                                    */
/*  State management for the full dApp creation pipeline:             */
/*  1. Template selection / AI prompt                                 */
/*  2. Chain & framework configuration                                */
/*  3. Visual layout builder (drag & drop)                            */
/*  4. AI generation                                                  */
/*  5. Preview & deployment                                           */
/* ================================================================== */

/* ---- Built-in dApp Templates ------------------------------------ */

export const DAPP_TEMPLATES: DappTemplate[] = [
  {
    id: 'defi-swap',
    name: 'Token Swap',
    description: 'DEX-style token swap interface with liquidity pools',
    category: 'defi',
    chains: ['ethereum', 'polygon', 'base', 'arbitrum'],
    framework: 'react-vite',
    icon: '🔄',
    tags: ['swap', 'uniswap', 'amm', 'liquidity'],
    features: [
      'Wallet connect',
      'Token selector',
      'Price quotes',
      'Slippage settings',
      'Transaction history',
    ],
    setupTime: '~3 min',
  },
  {
    id: 'defi-staking',
    name: 'Staking Dashboard',
    description: 'Stake tokens, track rewards, and claim yields',
    category: 'defi',
    chains: ['ethereum', 'polygon', 'base', 'arbitrum'],
    framework: 'react-vite',
    icon: '📈',
    tags: ['staking', 'yield', 'rewards', 'farming'],
    features: ['Stake/unstake', 'Reward tracker', 'APY display', 'Lock periods', 'Claim button'],
    setupTime: '~3 min',
  },
  {
    id: 'nft-marketplace',
    name: 'NFT Marketplace',
    description: 'Buy, sell, and auction NFTs with collection pages',
    category: 'nft',
    chains: ['ethereum', 'polygon', 'base', 'solana'],
    framework: 'nextjs',
    icon: '🖼️',
    tags: ['nft', 'marketplace', 'collection', 'auction'],
    features: ['Collection grid', 'Mint page', 'Listing/bidding', 'Royalties', 'IPFS metadata'],
    setupTime: '~5 min',
  },
  {
    id: 'nft-minting',
    name: 'NFT Minting dApp',
    description: 'Mint page with allowlist, reveal, and public sale phases',
    category: 'nft',
    chains: ['ethereum', 'polygon', 'base', 'solana'],
    framework: 'react-vite',
    icon: '🎨',
    tags: ['mint', 'nft', 'drop', 'allowlist'],
    features: ['Mint counter', 'Phase display', 'Wallet gate', 'Progress bar', 'Reveal timer'],
    setupTime: '~2 min',
  },
  {
    id: 'dao-governance',
    name: 'DAO Dashboard',
    description: 'Governance voting, proposals, and treasury management',
    category: 'dao',
    chains: ['ethereum', 'polygon', 'arbitrum'],
    framework: 'nextjs',
    icon: '🏛️',
    tags: ['dao', 'governance', 'voting', 'proposals', 'treasury'],
    features: ['Proposal list', 'Vote casting', 'Delegation', 'Treasury view', 'Timelock status'],
    setupTime: '~5 min',
  },
  {
    id: 'gaming-inventory',
    name: 'Gaming Inventory',
    description: 'On-chain game item inventory with trading',
    category: 'gaming',
    chains: ['polygon', 'base', 'solana', 'sui', 'aptos'],
    framework: 'react-vite',
    icon: '🎮',
    tags: ['gaming', 'inventory', 'items', 'trade'],
    features: ['Item grid', 'Equip/unequip', 'Trade offers', 'Crafting UI', 'Leaderboard'],
    setupTime: '~4 min',
  },
  {
    id: 'social-profile',
    name: 'Web3 Social Profile',
    description: 'Decentralized social profile with posts and followers',
    category: 'social',
    chains: ['ethereum', 'polygon', 'base', 'solana'],
    framework: 'nextjs',
    icon: '👤',
    tags: ['social', 'profile', 'lens', 'farcaster'],
    features: [
      'Profile card',
      'Post feed',
      'Follow/unfollow',
      'ENS/SNS resolution',
      'Activity stream',
    ],
    setupTime: '~4 min',
  },
  {
    id: 'multichain-bridge',
    name: 'Cross-Chain Bridge',
    description: 'Bridge tokens between EVM chains with status tracking',
    category: 'defi',
    chains: ['ethereum', 'polygon', 'base', 'arbitrum', 'optimism'],
    framework: 'react-vite',
    icon: '🌉',
    tags: ['bridge', 'cross-chain', 'multichain'],
    features: [
      'Chain selector',
      'Token picker',
      'Bridge status',
      'Fee estimation',
      'Transaction tracker',
    ],
    setupTime: '~4 min',
  },
  {
    id: 'solana-token-launcher',
    name: 'Solana Token Launcher',
    description: 'Create and launch SPL tokens with metadata',
    category: 'defi',
    chains: ['solana'],
    framework: 'react-vite',
    icon: '🚀',
    tags: ['solana', 'spl', 'token', 'launch'],
    features: [
      'Token creator',
      'Metadata upload',
      'Airdrop tool',
      'Token info display',
      'Holder list',
    ],
    setupTime: '~3 min',
  },
  {
    id: 'sui-move-dapp',
    name: 'Sui Move dApp',
    description: 'Interactive Sui Move object explorer and transaction builder',
    category: 'custom',
    chains: ['sui'],
    framework: 'react-vite',
    icon: '💧',
    tags: ['sui', 'move', 'objects'],
    features: [
      'Object explorer',
      'Move call builder',
      'Transaction visualizer',
      'Owned objects',
      'Package viewer',
    ],
    setupTime: '~3 min',
  },
  {
    id: 'aptos-dapp',
    name: 'Aptos dApp Starter',
    description: 'Aptos Move module interaction with wallet integration',
    category: 'custom',
    chains: ['aptos'],
    framework: 'react-vite',
    icon: '🅰️',
    tags: ['aptos', 'move', 'modules'],
    features: [
      'Wallet connect',
      'Module explorer',
      'Entry functions',
      'Resource viewer',
      'Transaction feed',
    ],
    setupTime: '~3 min',
  },
  {
    id: 'defi-lending',
    name: 'Lending Protocol',
    description: 'Supply, borrow, and manage positions in a lending pool',
    category: 'defi',
    chains: ['ethereum', 'polygon', 'base', 'arbitrum'],
    framework: 'nextjs',
    icon: '🏦',
    tags: ['lending', 'borrow', 'aave', 'compound', 'interest'],
    features: [
      'Supply/withdraw',
      'Borrow/repay',
      'Health factor',
      'APY rates',
      'Liquidation alerts',
    ],
    setupTime: '~5 min',
  },
  {
    id: 'defi-dex',
    name: 'DEX Aggregator',
    description: 'Multi-source swap aggregator with best-price routing',
    category: 'defi',
    chains: ['ethereum', 'polygon', 'base', 'arbitrum', 'optimism'],
    framework: 'react-vite',
    icon: '📊',
    tags: ['dex', 'aggregator', 'routing', 'swap', 'best-price'],
    features: [
      'Route comparison',
      'Price impact',
      'Gas estimation',
      'Multi-hop swaps',
      'Limit orders',
    ],
    setupTime: '~4 min',
  },
  {
    id: 'nft-launchpad',
    name: 'NFT Launchpad',
    description: 'Launch NFT collections with whitelist and tiered pricing',
    category: 'nft',
    chains: ['ethereum', 'polygon', 'base', 'solana'],
    framework: 'nextjs',
    icon: '🎯',
    tags: ['launchpad', 'nft', 'whitelist', 'presale'],
    features: [
      'Whitelist management',
      'Tiered pricing',
      'Countdown timer',
      'Batch minting',
      'Reveal mechanics',
    ],
    setupTime: '~5 min',
  },
  {
    id: 'dao-treasury',
    name: 'Treasury Manager',
    description: 'Multi-sig treasury dashboard with spending proposals',
    category: 'dao',
    chains: ['ethereum', 'polygon', 'arbitrum'],
    framework: 'nextjs',
    icon: '💰',
    tags: ['treasury', 'dao', 'multisig', 'budget'],
    features: [
      'Asset overview',
      'Spending proposals',
      'Multi-sig approvals',
      'Budget allocation',
      'Transaction history',
    ],
    setupTime: '~5 min',
  },
  {
    id: 'gaming-battle',
    name: 'PvP Battle Arena',
    description: 'On-chain PvP game with matchmaking and rewards',
    category: 'gaming',
    chains: ['polygon', 'base', 'solana', 'sui'],
    framework: 'react-vite',
    icon: '⚔️',
    tags: ['gaming', 'pvp', 'battle', 'matchmaking'],
    features: [
      'Matchmaking queue',
      'Battle interface',
      'Reward claims',
      'Leaderboard',
      'Character stats',
    ],
    setupTime: '~5 min',
  },
  {
    id: 'social-feed',
    name: 'Decentralized Feed',
    description: 'Social feed with posts, reactions, and token-gated communities',
    category: 'social',
    chains: ['ethereum', 'polygon', 'base', 'solana'],
    framework: 'nextjs',
    icon: '📱',
    tags: ['social', 'feed', 'posts', 'community', 'token-gated'],
    features: [
      'Post creation',
      'Reactions/likes',
      'Token-gated access',
      'Thread replies',
      'Media embeds',
    ],
    setupTime: '~4 min',
  },
  {
    id: 'payments-pos',
    name: 'Crypto Point-of-Sale',
    description: 'Accept crypto payments with QR codes and receipt tracking',
    category: 'defi',
    chains: ['ethereum', 'polygon', 'base', 'solana'],
    framework: 'react-vite',
    icon: '💳',
    tags: ['payments', 'pos', 'merchant', 'qr-code'],
    features: [
      'QR code generation',
      'Multi-token accept',
      'Receipt history',
      'Fiat conversion',
      'Webhook notifications',
    ],
    setupTime: '~3 min',
  },
];

/* ---- Default Theme ---------------------------------------------- */

const DEFAULT_THEME: DappTheme = {
  primaryColor: '#6366f1',
  backgroundColor: '#0f0f0f',
  fontFamily: 'Inter',
  borderRadius: 'md',
  mode: 'dark',
};

/* ---- Store Interface -------------------------------------------- */

interface DappBuilderState {
  /* Wizard step */
  step: DappBuilderStep;
  isOpen: boolean;

  /* Configuration */
  selectedTemplate: DappTemplate | null;
  framework: DappFramework;
  chains: DappChain[];
  category: DappCategory;
  projectName: string;
  aiPrompt: string;
  theme: DappTheme;

  /* Visual builder */
  pages: DappPage[];
  activePage: string | null;
  selectedComponent: string | null;
  draggedComponentType: string | null;

  /* Generation */
  isGenerating: boolean;
  generatedFiles: DappGeneratedFile[];
  generationError: string | null;
  currentProject: DappProject | null;

  /* Actions */
  open: () => void;
  close: () => void;
  setStep: (step: DappBuilderStep) => void;
  selectTemplate: (tpl: DappTemplate | null) => void;
  setFramework: (fw: DappFramework) => void;
  setChains: (chains: DappChain[]) => void;
  toggleChain: (chain: DappChain) => void;
  setCategory: (cat: DappCategory) => void;
  setProjectName: (name: string) => void;
  setAiPrompt: (prompt: string) => void;
  setTheme: (theme: Partial<DappTheme>) => void;

  /* Visual builder actions */
  addPage: (name: string, path: string) => void;
  removePage: (pageId: string) => void;
  setActivePage: (pageId: string) => void;
  addComponent: (pageId: string, component: DappComponentBlock) => void;
  removeComponent: (pageId: string, componentId: string) => void;
  updateComponent: (
    pageId: string,
    componentId: string,
    updates: Partial<DappComponentBlock>,
  ) => void;
  moveComponent: (pageId: string, componentId: string, newSlot: DappComponentBlock['slot']) => void;
  setDraggedComponentType: (type: string | null) => void;
  setSelectedComponent: (id: string | null) => void;

  /* Generation actions */
  generateFromTemplate: () => Promise<void>;
  generateFromAi: () => Promise<void>;
  generateFromVisual: () => Promise<void>;

  /* Reset */
  reset: () => void;
}

let pageCounter = 0;
let componentCounter = 0;

export const useDappBuilderStore = create<DappBuilderState>((set, get) => ({
  /* Initial state */
  step: 'choose-type',
  isOpen: false,

  selectedTemplate: null,
  framework: 'react-vite',
  chains: ['ethereum'],
  category: 'defi',
  projectName: '',
  aiPrompt: '',
  theme: DEFAULT_THEME,

  pages: [{ id: 'page-home', name: 'Home', path: '/', components: [] }],
  activePage: 'page-home',
  selectedComponent: null,
  draggedComponentType: null,

  isGenerating: false,
  generatedFiles: [],
  generationError: null,
  currentProject: null,

  /* -- Wizard lifecycle ------------------------------------------- */
  open: () => set({ isOpen: true, step: 'choose-type' }),
  close: () => set({ isOpen: false }),
  setStep: (step) => set({ step }),

  /* -- Configuration ---------------------------------------------- */
  selectTemplate: (tpl) => {
    if (tpl) {
      set({
        selectedTemplate: tpl,
        framework: tpl.framework,
        chains: tpl.chains.slice(0, 2) as DappChain[],
        category: tpl.category,
        projectName: tpl.name.toLowerCase().replace(/\s+/g, '-'),
      });
    } else {
      set({ selectedTemplate: null });
    }
  },
  setFramework: (framework) => set({ framework }),
  setChains: (chains) => set({ chains }),
  toggleChain: (chain) => {
    const current = get().chains;
    if (current.includes(chain)) {
      if (current.length > 1) set({ chains: current.filter((c) => c !== chain) });
    } else {
      set({ chains: [...current, chain] });
    }
  },
  setCategory: (category) => set({ category }),
  setProjectName: (projectName) => set({ projectName }),
  setAiPrompt: (aiPrompt) => set({ aiPrompt }),
  setTheme: (partial) => set((s) => ({ theme: { ...s.theme, ...partial } })),

  /* -- Visual builder --------------------------------------------- */
  addPage: (name, path) => {
    const id = `page-${++pageCounter}`;
    set((s) => ({ pages: [...s.pages, { id, name, path, components: [] }] }));
  },
  removePage: (pageId) => {
    set((s) => ({
      pages: s.pages.filter((p) => p.id !== pageId),
      activePage: s.activePage === pageId ? (s.pages[0]?.id ?? null) : s.activePage,
    }));
  },
  setActivePage: (pageId) => set({ activePage: pageId }),
  addComponent: (pageId, component) => {
    const id = `comp-${++componentCounter}`;
    const newComp = { ...component, id };
    set((s) => ({
      pages: s.pages.map((p) =>
        p.id === pageId ? { ...p, components: [...p.components, newComp] } : p,
      ),
    }));
  },
  removeComponent: (pageId, componentId) => {
    set((s) => ({
      pages: s.pages.map((p) =>
        p.id === pageId
          ? { ...p, components: p.components.filter((c) => c.id !== componentId) }
          : p,
      ),
      selectedComponent: get().selectedComponent === componentId ? null : get().selectedComponent,
    }));
  },
  updateComponent: (pageId, componentId, updates) => {
    set((s) => ({
      pages: s.pages.map((p) =>
        p.id === pageId
          ? {
              ...p,
              components: p.components.map((c) =>
                c.id === componentId ? { ...c, ...updates } : c,
              ),
            }
          : p,
      ),
    }));
  },
  moveComponent: (pageId, componentId, newSlot) => {
    set((s) => ({
      pages: s.pages.map((p) =>
        p.id === pageId
          ? {
              ...p,
              components: p.components.map((c) =>
                c.id === componentId ? { ...c, slot: newSlot } : c,
              ),
            }
          : p,
      ),
    }));
  },
  setDraggedComponentType: (type) => set({ draggedComponentType: type }),
  setSelectedComponent: (id) => set({ selectedComponent: id }),

  /* -- Generation ------------------------------------------------- */
  generateFromTemplate: async () => {
    const { selectedTemplate, projectName, framework, chains } = get();
    if (!selectedTemplate) return;
    set({ isGenerating: true, generationError: null, step: 'generate' });
    try {
      const res = await dappService.scaffoldFromTemplate(
        selectedTemplate.id,
        projectName || selectedTemplate.name,
        framework,
        chains,
      );
      set({ generatedFiles: res.files, step: 'preview', isGenerating: false });
    } catch (e: unknown) {
      set({
        generationError: e instanceof Error ? e.message : 'Generation failed',
        isGenerating: false,
        step: 'generate',
      });
    }
  },

  generateFromAi: async () => {
    const { aiPrompt, framework, chains, category } = get();
    if (!aiPrompt.trim()) return;
    set({ isGenerating: true, generationError: null, step: 'generate' });
    try {
      const res = await dappService.generateDapp({
        prompt: aiPrompt,
        framework,
        chains,
        category,
      });
      set({ generatedFiles: res.files, step: 'preview', isGenerating: false });
    } catch (e: unknown) {
      set({
        generationError: e instanceof Error ? e.message : 'Generation failed',
        isGenerating: false,
        step: 'generate',
      });
    }
  },

  generateFromVisual: async () => {
    const { pages, framework, chains, category, aiPrompt } = get();
    set({ isGenerating: true, generationError: null, step: 'generate' });
    try {
      const res = await dappService.generateDapp({
        prompt: aiPrompt || 'Generate a dApp based on the visual layout',
        framework,
        chains,
        category,
        layout: pages,
      });
      set({ generatedFiles: res.files, step: 'preview', isGenerating: false });
    } catch (e: unknown) {
      set({
        generationError: e instanceof Error ? e.message : 'Generation failed',
        isGenerating: false,
        step: 'generate',
      });
    }
  },

  /* -- Reset ------------------------------------------------------ */
  reset: () =>
    set({
      step: 'choose-type',
      isOpen: false,
      selectedTemplate: null,
      framework: 'react-vite',
      chains: ['ethereum'],
      category: 'defi',
      projectName: '',
      aiPrompt: '',
      theme: DEFAULT_THEME,
      pages: [{ id: 'page-home', name: 'Home', path: '/', components: [] }],
      activePage: 'page-home',
      selectedComponent: null,
      draggedComponentType: null,
      isGenerating: false,
      generatedFiles: [],
      generationError: null,
      currentProject: null,
    }),
}));
